import styled from '@emotion/styled';
import { colors, Text, Tooltip } from '@innovationdepartment/proxima-ui';
import { InfoOutlined } from '@mui/icons-material';
import { useMemo } from 'react';
import FilterRules from '../../components/FilterRules/FilterRules';
import { SqlPanel, SqlToggleButton, useSqlPreview } from '../../components/FilterRules/SqlPreview';
import { PanelSectionHeading } from '../../components/form.styles';
import { describeSeedSplit, seedSplit } from '../../lib/brandShare';
import { DEFAULT_WINDOW_LABEL } from '../../lib/describeFilters';
import { displayFilters, pickFilters } from '../../lib/filters';
import { flatToTree } from '../../lib/queryMapping';
import type { AudienceDefinition } from '../../types';
import { DetailsSection, SectionTitle } from './DefinitionDetails.styles';

const RULES_FIXED_NOTE = 'Rules are fixed once created. Clone to change them.';

const RulesTitleGroup = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const RulesInfo = styled.span`
  display: inline-flex;
  color: ${colors.neutral50};
  cursor: help;

  svg {
    font-size: 16px;
  }
`;

type DefinitionTabProps = {
  definition: AudienceDefinition;
  errorMessage: string | null;
};

/** Read-only rules through the wizard's renderer, with the rules-as-SQL toggle and the brand share. */
const DefinitionTab = ({ definition, errorMessage }: DefinitionTabProps) => {
  const query = useMemo(() => flatToTree(displayFilters(pickFilters(definition))), [definition]);
  const sqlPreview = useSqlPreview(query);
  return (
    <DetailsSection aria-labelledby="definition-rules-heading">
      <SectionTitle>
        <RulesTitleGroup>
          <PanelSectionHeading id="definition-rules-heading">Rules</PanelSectionHeading>
          <Tooltip title={RULES_FIXED_NOTE}>
            <RulesInfo tabIndex={0} role="img" aria-label={RULES_FIXED_NOTE}>
              <InfoOutlined />
            </RulesInfo>
          </Tooltip>
        </RulesTitleGroup>
        <SqlToggleButton preview={sqlPreview} />
      </SectionTitle>
      {query.rules.length === 0 ? (
        <Text variant="body2" color="neutral60">
          No rules: {DEFAULT_WINDOW_LABEL}
        </Text>
      ) : null}
      <FilterRules query={query} disabled bordered={false} />
      <Text variant="body2" color="neutral60">
        Brand share {definition.brandSharePercent}%.{' '}
        {describeSeedSplit(
          seedSplit(definition.uniqueIndividualLimit, definition.brandSharePercent)
        )}
      </Text>
      <SqlPanel preview={sqlPreview} />
      {errorMessage ? (
        <Text variant="body2" color="red60">
          {errorMessage}
        </Text>
      ) : null}
    </DetailsSection>
  );
};

export default DefinitionTab;
